/**
 * REST client helpers for GitHub work-item and artifact work-item link endpoints.
 */
import { buildBackendUrl } from '@/lib/auth-routing';
import type {
  ArtifactWorkItemLink,
  ArtifactWorkItemLinkListResponse,
  CreateArtifactWorkItemLinkBody,
  GitHubWorkItem,
  GitHubWorkItemDetailResponse,
  GitHubWorkItemListResponse,
  GitHubWorkItemState,
  SuccessResponse,
} from './api';

export interface GitHubWorkItemListFilters {
  repoOwner?: string;
  repoName?: string;
  issueNumber?: number;
  state?: GitHubWorkItemState;
  workAreaId?: string;
  homeCodebaseId?: string;
  limit?: number;
  offset?: number;
}

function buildQuery(filters: GitHubWorkItemListFilters): string {
  const params = new URLSearchParams();
  if (filters.repoOwner) params.set('repoOwner', filters.repoOwner);
  if (filters.repoName) params.set('repoName', filters.repoName);
  if (filters.issueNumber !== undefined) params.set('issueNumber', String(filters.issueNumber));
  if (filters.state) params.set('state', filters.state);
  if (filters.workAreaId) params.set('workAreaId', filters.workAreaId);
  if (filters.homeCodebaseId) params.set('homeCodebaseId', filters.homeCodebaseId);
  if (filters.limit !== undefined) params.set('limit', String(filters.limit));
  if (filters.offset !== undefined) params.set('offset', String(filters.offset));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

async function readApiError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string; message?: string };
    return body.error ?? body.message ?? `API error ${String(res.status)}`;
  } catch {
    return `API error ${String(res.status)}`;
  }
}

async function fetchOrThrow(url: string, init?: RequestInit): Promise<Response> {
  const res = await fetch(buildBackendUrl(url), {
    ...init,
    credentials: init?.credentials ?? 'include',
  });
  if (!res.ok) {
    const err = new Error(await readApiError(res)) as Error & { status: number };
    err.status = res.status;
    throw err;
  }
  return res;
}

function jsonRequest(body: unknown): RequestInit {
  return {
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  };
}

export async function listWorkItems(
  filters: GitHubWorkItemListFilters = {}
): Promise<GitHubWorkItemListResponse> {
  const res = await fetchOrThrow(`/api/github-work-items${buildQuery(filters)}`);
  return (await res.json()) as GitHubWorkItemListResponse;
}

export async function findWorkItemByRepoIssue(
  owner: string,
  repo: string,
  issueNumber: number
): Promise<GitHubWorkItem | null> {
  const body = await listWorkItems({
    repoOwner: owner,
    repoName: repo,
    issueNumber,
    limit: 2,
  });

  if (body.workItems.length === 0) return null;
  if (body.workItems.length === 1) return body.workItems[0];

  console.error('Expected at most one GitHub work item for repo issue', {
    owner,
    repo,
    issueNumber,
    count: body.workItems.length,
    workItemIds: body.workItems.map(workItem => workItem.id),
  });
  throw new Error('Unexpected duplicate work items — contact an admin');
}

export async function listWorkItemsForWorkArea(
  workAreaId: string
): Promise<GitHubWorkItemListResponse> {
  const res = await fetchOrThrow(`/api/work-areas/${encodeURIComponent(workAreaId)}/work-items`);
  return (await res.json()) as GitHubWorkItemListResponse;
}

export async function getWorkItem(workItemId: string): Promise<GitHubWorkItemDetailResponse> {
  const res = await fetchOrThrow(`/api/github-work-items/${encodeURIComponent(workItemId)}`);
  return (await res.json()) as GitHubWorkItemDetailResponse;
}

export async function reconcileWorkItem(workItemId: string): Promise<GitHubWorkItem> {
  const res = await fetchOrThrow(
    `/api/github-work-items/${encodeURIComponent(workItemId)}/reconcile`,
    { method: 'POST' }
  );
  return (await res.json()) as GitHubWorkItem;
}

export async function listArtifactWorkItemLinks(
  artifactId: string
): Promise<ArtifactWorkItemLink[]> {
  const res = await fetchOrThrow(
    `/api/artifacts/${encodeURIComponent(artifactId)}/work-item-links`
  );
  const body = (await res.json()) as ArtifactWorkItemLinkListResponse;
  return body.links;
}

export async function createArtifactWorkItemLink(
  artifactId: string,
  body: CreateArtifactWorkItemLinkBody
): Promise<ArtifactWorkItemLink> {
  const res = await fetchOrThrow(
    `/api/artifacts/${encodeURIComponent(artifactId)}/work-item-links`,
    {
      method: 'POST',
      ...jsonRequest(body),
    }
  );
  const data = (await res.json()) as { link: ArtifactWorkItemLink };
  return data.link;
}

export async function deleteArtifactWorkItemLink(linkId: string): Promise<SuccessResponse> {
  const res = await fetchOrThrow(`/api/artifact-work-item-links/${encodeURIComponent(linkId)}`, {
    method: 'DELETE',
  });
  return (await res.json()) as SuccessResponse;
}
